import React, { useState } from 'react';
import { SearchIcon, RocketIcon, SparklesIcon, CheckCircle2Icon, ArrowRightIcon, Sparkles } from 'lucide-react';
import { TeamSection } from './TeamSection';
export function CardSection() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const roadmapSteps = [{
    number: 1,
    emoji: '✍️',
    title: 'Register',
    description: 'Provide name & email to secure your starting position on the road.',
    color: 'bg-[#b8c5d6]',
    barColor: 'bg-[#b8c5d6]',
    position: 'top'
  }, {
    number: 2,
    emoji: '🤝',
    title: 'Join a Team',
    description: "Decide if you'll go solo or form a powerful 3-5 member crew.",
    color: 'bg-[#d4d9b8]',
    barColor: 'bg-[#d4d9b8]',
    position: 'bottom'
  }, {
    number: 3,
    emoji: '🧭',
    title: 'Pick a Track',
    description: 'Choose your direction: NORTH (open to all) or EAST/SOUTH/WEST (members only).',
    color: 'bg-[#c98a8a]',
    barColor: 'bg-[#c98a8a]',
    position: 'top'
  }, {
    number: 4,
    emoji: '💻',
    title: 'Build Your Prototype',
    description: 'Create a real, shippable AI product using the provided tools and resources.',
    color: 'bg-[#a8d4c4]',
    barColor: 'bg-[#a8d4c4]',
    position: 'bottom'
  }, {
    number: 5,
    emoji: '💬',
    title: 'Get Feedback',
    description: 'Tune your final product with direct, strategic feedback from senior AI PMs.',
    color: 'bg-[#d9b8c5]',
    barColor: 'bg-[#d9b8c5]',
    position: 'top'
  }, {
    number: 6,
    emoji: '🏆',
    title: 'Showcase & Win!',
    description: 'Top 5 featured; everyone earns a certificate & job board access.',
    isHighlighted: true,
    color: 'bg-[#ff8a14]',
    barColor: 'bg-[#ff8a14]',
    position: 'bottom'
  }];
  const faqs = [{
    question: 'Who We Are – The Product Compass',
    answer: <div className="space-y-3" data-id="element-11">
          <p data-id="element-12">
            We are <span className="font-semibold" data-id="element-13">The Product Compass</span>,
            the #1 AI + Product Management newsletter. We help PMs and
            innovators harness AI to turn ideas into real products, fast.
          </p>
          <p data-id="element-14">
            With step-by-step no fluff playbooks, templates, and frameworks,
            hands-on experiences you can build PRDs, prototypes, and roadmaps
            that actually work.
          </p>
          <p data-id="element-15">
            Trusted by{' '}
            <span className="font-semibold" data-id="element-16">
              129,000+ product pros worldwide
            </span>{' '}
            and curated by PM veteran{' '}
            <span className="font-semibold" data-id="element-17">Paweł Huryn</span>, we are your
            AI-powered compass for navigating product challenges, creating real
            impact, and making ideas matter.
          </p>
        </div>
  }, {
    question: 'Who can join the AI PM Prototron?',
    answer: <div className="space-y-2" data-id="element-18">
          <p data-id="element-19">
            <span className="font-semibold" data-id="element-20">
              Members of The Product Compass:
            </span>{' '}
            Join any of the 3 tracks — completely free.
          </p>
          <p data-id="element-21">
            <span className="font-semibold" data-id="element-22">Non-members:</span> You're welcome
            to participate in Track 1: AI Product Thinker — also free.
          </p>
          <p className="text-[#606060] italic mt-3" data-id="element-23">
            💡 Not a member yet? Join The Product Compass to unlock all tracks,
            mentorship, and career perks.
          </p>
        </div>
  }, {
    question: 'What are the 3 tracks?',
    answer: <div className="space-y-2" data-id="element-24">
          <p className="mb-3" data-id="element-25">
            Choose your starting point. Complete your PRD in just one week—then
            decide whether to stop with a career-ready artifact… or go further
            and build something that actually works.
          </p>
          <ul className="space-y-2 ml-4" data-id="element-26">
            <li className="flex items-start" data-id="element-27">
              <span className="text-[#ff8a14] mr-2" data-id="element-28">•</span>
              <span data-id="element-29">
                <strong data-id="element-30">Track 1: AI Product Thinker</strong> — Build your first
                AI PRD that hiring managers will actually read (open to
                everyone, 1 week)
              </span>
            </li>
            <li className="flex items-start" data-id="element-31">
              <span className="text-[#ff8a14] mr-2" data-id="element-32">•</span>
              <span data-id="element-33">
                <strong data-id="element-34">Track 2: AI Agent Builder</strong> — Ship a working AI
                agent using n8n—no engineering degree required (members only,
                2-3 weeks)
              </span>
            </li>
            <li className="flex items-start" data-id="element-35">
              <span className="text-[#ff8a14] mr-2" data-id="element-36">•</span>
              <span data-id="element-37">
                <strong data-id="element-38">Track 3: AI Agent Architect</strong> — Design systems
                where AI agents collaborate like a human team (members only, 2-3
                weeks)
              </span>
            </li>
          </ul>
          <p className="text-[#606060] italic mt-3" data-id="element-39">
            🔒 Tracks 2 & 3 are exclusive to The Product Compass premium
            members.
          </p>
        </div>
  }, {
    question: 'Is there a fee?',
    answer: <p data-id="element-40">
          No — it's 100% free for everyone. Premium members get full access;
          non-members can build in Track 1: AI Product Thinker at no cost.
        </p>
  }, {
    question: "What if I don't have a team?",
    answer: <div className="space-y-2" data-id="element-41">
          <p className="mb-2" data-id="element-42">No problem!</p>
          <ul className="space-y-2 ml-4" data-id="element-43">
            <li className="flex items-start" data-id="element-44">
              <span className="text-[#ff8a14] mr-2" data-id="element-45">•</span>
              <span data-id="element-46">
                Join our matchmaking channel during registration to find
                teammates.
              </span>
            </li>
            <li className="flex items-start" data-id="element-47">
              <span className="text-[#ff8a14] mr-2" data-id="element-48">•</span>
              <span data-id="element-49">
                Many PMs participate solo — your idea and execution matter most.
              </span>
            </li>
          </ul>
        </div>
  }, {
    question: 'Will there be support while I build?',
    answer: <div className="space-y-2" data-id="element-50">
          <p className="mb-2" data-id="element-51">Yes! You'll get:</p>
          <ul className="space-y-2 ml-4" data-id="element-52">
            <li className="flex items-start" data-id="element-53">
              <span className="text-[#ff8a14] mr-2" data-id="element-54">•</span>
              <span data-id="element-55">Live office hours with senior PMs</span>
            </li>
            <li className="flex items-start" data-id="element-56">
              <span className="text-[#ff8a14] mr-2" data-id="element-57">•</span>
              <span data-id="element-58">Dedicated Slack channel for real-time help</span>
            </li>
            <li className="flex items-start" data-id="element-59">
              <span className="text-[#ff8a14] mr-2" data-id="element-60">•</span>
              <span data-id="element-61">Mentor feedback on your prototype</span>
            </li>
          </ul>
          <p className="text-[#606060] italic mt-3" data-id="element-62">
            No question is too small — we're here to unblock you.
          </p>
        </div>
  }, {
    question: 'Do all tracks have the same prizes and recognition?',
    answer: <div className="space-y-2" data-id="element-63">
          <p className="mb-2" data-id="element-64">
            Yes! All tracks are judged equally. Top participants from any track
            receive:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-65">
            <li className="flex items-start" data-id="element-66">
              <span className="text-[#ff8a14] mr-2" data-id="element-67">🌟</span>
              <span data-id="element-68">
                Featured spot in the AI PM Talent Showcase (shared with
                recruiters)
              </span>
            </li>
            <li className="flex items-start" data-id="element-69">
              <span className="text-[#ff8a14] mr-2" data-id="element-70">📝</span>
              <span data-id="element-71">Personalized feedback from senior product leaders</span>
            </li>
            <li className="flex items-start" data-id="element-72">
              <span className="text-[#ff8a14] mr-2" data-id="element-73">🏅</span>
              <span data-id="element-74">Official certificate + "Built with AI" digital badge</span>
            </li>
            <li className="flex items-start" data-id="element-75">
              <span className="text-[#ff8a14] mr-2" data-id="element-76">💼</span>
              <span data-id="element-77">Priority access to partner job opportunities</span>
            </li>
          </ul>
        </div>
  }, {
    question: 'When are winners announced?',
    answer: <p data-id="element-78">Within 1 week after the submission deadline.</p>
  }, {
    question: "How do I prove I'm The Product Compass member?",
    answer: <p data-id="element-79">
          We'll verify your membership automatically when you register using
          your Product Compass Substack email. No extra steps needed.
        </p>
  }, {
    question: 'Can I upgrade to full access during the event?',
    answer: <p data-id="element-80">
          Yes! Join The Product Compass anytime during the event to unlock
          Tracks 2 & 3 and switch tracks if you'd like.
        </p>
  }, {
    question: 'Still unsure which track to pick?',
    answer: <div className="space-y-2" data-id="element-81">
          <p className="mb-2" data-id="element-82">We'll host a Track Selection Workshop with:</p>
          <ul className="space-y-2 ml-4" data-id="element-83">
            <li className="flex items-start" data-id="element-84">
              <span className="text-[#ff8a14] mr-2" data-id="element-85">•</span>
              <span data-id="element-86">Real project examples</span>
            </li>
            <li className="flex items-start" data-id="element-87">
              <span className="text-[#ff8a14] mr-2" data-id="element-88">•</span>
              <span data-id="element-89">Recommended tools (n8n, Lovable, etc.)</span>
            </li>
            <li className="flex items-start" data-id="element-90">
              <span className="text-[#ff8a14] mr-2" data-id="element-91">•</span>
              <span data-id="element-92">Live Q&A with PM mentors</span>
            </li>
          </ul>
        </div>
  }];
  return <div className="w-full bg-gradient-to-b from-[#f3f3f7] to-[#e6eef8] py-16" data-id="element-93">
      <div className="max-w-5xl mx-auto px-4" data-id="element-94">
        <div className="text-center mb-16" data-id="element-95">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2f5f98] mb-4" data-id="element-96">
            Learn slow alone? Build fast together.
          </h2>
          <p className="text-[#606060] max-w-3xl mx-auto text-lg" data-id="element-97">
            Join a community of ambitious product managers building AI - powered
            prototypes that solve real problems and showcase your skills to
            potential employers.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-98">
          <div className="p-8 border-l-4 border-[#8bafcf] shadow-md bg-white rounded-lg transform hover:scale-105 transition-transform" data-id="element-99">
            <div className="flex items-start mb-4" data-id="element-100">
              <SearchIcon className="w-8 h-8 text-[#8bafcf] mr-4 flex-shrink-0" data-id="element-101" />
              <div data-id="element-102">
                <h3 className="font-bold text-xl mb-2 text-[#1a1a1a]" data-id="element-103">
                  Job Search Struggles?
                </h3>
                <p className="text-[#202929]" data-id="element-104">
                  Tired of your resume getting lost in the ATS black hole? Our
                  hackathon gives you a concrete project to showcase your AI
                  skills and stand out from the crowd.
                </p>
              </div>
            </div>
            <div className="pl-12 mt-4" data-id="element-105">
              <p className="flex items-center text-[#606060]" data-id="element-106">
                <CheckCircle2Icon className="w-5 h-5 text-[#8bafcf] mr-2" data-id="element-107" />
                <span data-id="element-108">Add a real AI project to your portfolio</span>
              </p>
              <p className="flex items-center text-[#606060]" data-id="element-109">
                <CheckCircle2Icon className="w-5 h-5 text-[#8bafcf] mr-2" data-id="element-110" />
                <span data-id="element-111">Get featured on our job board</span>
              </p>
            </div>
          </div>
          <div className="p-8 border-l-4 border-[#5590bc] shadow-md bg-white rounded-lg transform hover:scale-105 transition-transform" data-id="element-112">
            <div className="flex items-start mb-4" data-id="element-113">
              <RocketIcon className="w-8 h-8 text-[#5590bc] mr-4 flex-shrink-0" data-id="element-114" />
              <div data-id="element-115">
                <h3 className="font-bold text-xl mb-2 text-[#1a1a1a]" data-id="element-116">
                  Ready for Promotion?
                </h3>
                <p className="text-[#202929]" data-id="element-117">
                  Need to prove you can think and act like an AI PM? Build a
                  project that demonstrates your strategic thinking and
                  technical understanding.
                </p>
              </div>
            </div>
            <div className="pl-12 mt-4" data-id="element-118">
              <p className="flex items-center text-[#606060]" data-id="element-119">
                <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-2" data-id="element-120" />
                <span data-id="element-121">Get mentored by senior AI PMs</span>
              </p>
              <p className="flex items-center text-[#606060]" data-id="element-122">
                <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-2" data-id="element-123" />
                <span data-id="element-124">Receive a certificate of completion</span>
              </p>
            </div>
          </div>
        </div>
        <div className="p-8 border-l-4 border-[#2e5f99] shadow-md max-w-3xl mx-auto bg-white rounded-lg mb-16 transform hover:scale-105 transition-transform" data-id="element-125">
          <div className="flex items-start mb-4" data-id="element-126">
            <SparklesIcon className="w-8 h-8 text-[#2e5f99] mr-4 flex-shrink-0" data-id="element-127" />
            <div data-id="element-128">
              <h3 className="font-bold text-xl mb-2 text-[#1a1a1a]" data-id="element-129">
                Switching to AI Product Management?
              </h3>
              <p className="text-[#202929]" data-id="element-130">
                Breaking into AI Product Management doesn't have to be a
                mystery. Our structured hackathon provides the guidance,
                network, and portfolio piece you need.
              </p>
            </div>
          </div>
          <div className="pl-12 mt-4" data-id="element-131">
            <p className="flex items-center text-[#606060]" data-id="element-132">
              <CheckCircle2Icon className="w-5 h-5 text-[#2e5f99] mr-2" data-id="element-133" />
              <span data-id="element-134">Learn AI PM frameworks and methodologies</span>
            </p>
            <p className="flex items-center text-[#606060]" data-id="element-135">
              <CheckCircle2Icon className="w-5 h-5 text-[#2e5f99] mr-2" data-id="element-136" />
              <span data-id="element-137">Connect with hiring managers in the AI space</span>
            </p>
          </div>
        </div>
        {/* Testimonial */}
        <div className="bg-white p-8 rounded-lg shadow-md mb-16 relative" data-id="element-138">
          <div className="text-6xl text-[#2f5f98] opacity-20 font-serif absolute top-4 left-6" data-id="element-139">
            "
          </div>
          <div className="relative z-10" data-id="element-140">
            <p className="text-lg text-[#1a1a1a] italic mb-6" data-id="element-141">
              I've read a ton of AI stuff, but The Product Compass is the only
              place that gives me clear, no fluff frameworks I can actually use
              immediately. Plus, the community isn't just hype—it's real people
              building real things.
            </p>
            <div className="flex items-center" data-id="element-142">
              <div className="w-12 h-12 bg-gradient-to-br from-[#5590bc] to-[#2f5f98] rounded-full mr-4 flex items-center justify-center" data-id="element-143">
                <span className="text-white font-bold" data-id="element-144">PC</span>
              </div>
              <div data-id="element-145">
                <p className="font-bold text-[#2f5f98]" data-id="element-146">Community Consensus</p>
                <p className="text-sm text-[#606060]" data-id="element-147">
                  Based on notes of 129K+ subscribers and 350K+ followers across
                  Substack and LinkedIn
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Choose Your Track Section - WITH DURATION FOOTER */}
      <div className="w-full bg-gradient-to-b from-[#dce9f5] to-[#c8dce8] py-20 px-4" data-id="element-148">
        <div className="max-w-7xl mx-auto" data-id="element-149">
          <div className="text-center mb-16" data-id="element-150">
            <h2 className="text-5xl md:text-6xl font-bold text-[#2f5f98] mb-6 leading-tight" data-id="element-151">
              Pick Your Track
            </h2>
            <p className="text-[#4a5568] text-xl max-w-4xl mx-auto mb-4 leading-relaxed" data-id="element-152">
              Select the level that matches your skills and ambitions—each track
              offers unique AI PM challenges with increasing complexity.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-stretch" data-id="element-153">
            {/* TRACK 1: AI Product Thinker */}
            <div className="bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all relative border-4 border-[#ff8a14] flex flex-col h-full overflow-visible" data-id="element-154">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-white text-[#ff8a14] border-2 border-[#ff8a14] px-5 py-2 rounded-full text-sm font-bold shadow-lg z-10" data-id="element-155">
                FREE ACCESS
              </div>

              <div className="p-10 flex flex-col flex-grow" data-id="element-156">
                <div className="inline-block bg-[#ff8a14] bg-opacity-10 px-4 py-2 rounded-full mb-4" data-id="element-157">
                  <span className="text-[#ff8a14] font-bold text-base" data-id="element-158">
                    TRACK 1
                  </span>
                </div>
                <h3 className="text-3xl font-bold text-[#2f5f98] mb-3 leading-tight" data-id="element-159">
                  AI Product Thinker
                </h3>
                <p className="text-[#1a1a1a] font-bold text-lg mb-4 leading-snug" data-id="element-160">
                  → Build your first AI PRD
                </p>
                <div className="flex-grow min-h-[180px]" data-id="element-161">
                  <p className="text-[#4a5568] leading-relaxed mb-3" data-id="element-162">
                    Perfect if you're new to AI or want to validate your product
                    instincts before coding anything.
                  </p>
                  <p className="text-[#4a5568] leading-relaxed mb-8" data-id="element-163">
                    You'll define a real user problem, design an AI-powered
                    solution, and deliver a portfolio ready PRD that
                    demonstrates systems level thinking and user centered AI
                    design
                  </p>
                </div>
              </div>

              <div className="px-10 pb-6 mt-auto" data-id="element-164">
                <div className="bg-[#f8fafb] p-6 rounded-xl border-2 border-[#ff8a14] border-opacity-20 min-h-[240px]" data-id="element-165">
                  <p className="text-[#2f5f98] font-bold text-base mb-4" data-id="element-166">
                    What you gain:
                  </p>
                  <ul className="space-y-3" data-id="element-167">
                    <li className="flex items-start" data-id="element-168">
                      <CheckCircle2Icon className="w-5 h-5 text-[#ff8a14] mr-3 flex-shrink-0 mt-0.5" data-id="element-169" />
                      <span className="text-[#4a5568] font-medium" data-id="element-170">
                        A portfolio-ready AI PRD that shows strategic depth
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-171">
                      <CheckCircle2Icon className="w-5 h-5 text-[#ff8a14] mr-3 flex-shrink-0 mt-0.5" data-id="element-172" />
                      <span className="text-[#4a5568] font-medium" data-id="element-173">
                        Confidence to speak fluently about AI product tradeoffs
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-174">
                      <CheckCircle2Icon className="w-5 h-5 text-[#ff8a14] mr-3 flex-shrink-0 mt-0.5" data-id="element-175" />
                      <span className="text-[#4a5568] font-medium" data-id="element-176">
                        A foundation to transition into AI PM roles
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Duration Footer */}
              <div className="bg-gradient-to-r from-[#ff8a14] to-[#e67d12] px-10 py-5 border-t-4 border-[#ff8a14]" data-id="element-177">
                <p className="text-white text-center text-lg" data-id="element-178">
                  <span className="font-bold" data-id="element-179">1 week</span>
                  <span className="mx-2" data-id="element-180">•</span>
                  <span data-id="element-181">Open to all</span>
                </p>
              </div>
            </div>

            {/* TRACK 2: AI Agent Builder */}
            <div className="bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all relative border-4 border-[#5590bc] flex flex-col h-full overflow-visible" data-id="element-182">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-gradient-to-r from-[#ff8a14] to-[#e67d12] text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg flex items-center z-10" data-id="element-183">
                <span className="mr-1" data-id="element-184">🔒</span> MEMBERS ONLY
              </div>

              <div className="p-10 flex flex-col flex-grow" data-id="element-185">
                <div className="inline-block bg-[#5590bc] bg-opacity-10 px-4 py-2 rounded-full mb-4" data-id="element-186">
                  <span className="text-[#5590bc] font-bold text-base" data-id="element-187">
                    TRACK 2
                  </span>
                </div>
                <h3 className="text-3xl font-bold text-[#2f5f98] mb-3 leading-tight" data-id="element-188">
                  AI Agent Builder
                </h3>
                <p className="text-[#1a1a1a] font-bold text-lg mb-4 leading-snug" data-id="element-189">
                  → Ship a working AI agent—no engineering degree required
                </p>
                <div className="flex-grow min-h-[180px]" data-id="element-190">
                  <p className="text-[#4a5568] leading-relaxed mb-3" data-id="element-191">
                    For product managers ready to move from documentation to
                    real product delivery.
                  </p>
                  <p className="text-[#4a5568] leading-relaxed mb-8" data-id="element-192">
                    You will take your PRD—or refine it—and use n8n to build a
                    live, no-code AI agent that automates workflows, connects
                    tools, and solves actual operational problems.
                  </p>
                </div>
              </div>

              <div className="px-10 pb-6 mt-auto" data-id="element-193">
                <div className="bg-[#f8fafb] p-6 rounded-xl border-2 border-[#5590bc] border-opacity-20 min-h-[240px]" data-id="element-194">
                  <p className="text-[#2f5f98] font-bold text-base mb-4" data-id="element-195">
                    What you gain:
                  </p>
                  <ul className="space-y-3" data-id="element-196">
                    <li className="flex items-start" data-id="element-197">
                      <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-3 flex-shrink-0 mt-0.5" data-id="element-198" />
                      <span className="text-[#4a5568] font-medium" data-id="element-199">
                        A working AI agent you can demo to stakeholders, peers,
                        or hiring teams
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-200">
                      <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-3 flex-shrink-0 mt-0.5" data-id="element-201" />
                      <span className="text-[#4a5568] font-medium" data-id="element-202">
                        Hands-on fluency with AI automation platforms n8n
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-203">
                      <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-3 flex-shrink-0 mt-0.5" data-id="element-204" />
                      <span className="text-[#4a5568] font-medium" data-id="element-205">
                        Proof you can ship real AI solutions, not just define
                        them
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Duration Footer */}
              <div className="bg-gradient-to-r from-[#5590bc] to-[#3d7ba8] px-10 py-5 border-t-4 border-[#5590bc]" data-id="element-206">
                <p className="text-white text-center text-lg" data-id="element-207">
                  <span className="font-bold" data-id="element-208">2–3 weeks</span>
                  <span className="mx-2" data-id="element-209">•</span>
                  <span data-id="element-210">⭐Members only</span>
                </p>
              </div>
            </div>

            {/* TRACK 3: AI Agent Architect */}
            <div className="bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all relative border-4 border-[#2f5f98] flex flex-col h-full overflow-visible" data-id="element-211">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-gradient-to-r from-[#ff8a14] to-[#e67d12] text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg flex items-center z-10" data-id="element-212">
                <span className="mr-1" data-id="element-213">🔒</span> MEMBERS ONLY
              </div>

              <div className="p-10 flex flex-col flex-grow" data-id="element-214">
                <div className="inline-block bg-[#2f5f98] bg-opacity-10 px-4 py-2 rounded-full mb-4" data-id="element-215">
                  <span className="text-[#2f5f98] font-bold text-base" data-id="element-216">
                    TRACK 3
                  </span>
                </div>
                <h3 className="text-3xl font-bold text-[#2f5f98] mb-3 leading-tight" data-id="element-217">
                  AI Agent Architect
                </h3>
                <p className="text-[#1a1a1a] font-bold text-lg mb-4 leading-snug" data-id="element-218">
                  → Architect coordinated multi-agent AI systems that solve
                  complex problems.
                </p>
                <div className="flex-grow min-h-[180px]" data-id="element-219">
                  <p className="text-[#4a5568] leading-relaxed mb-3" data-id="element-220">
                    For PMs aiming to lead next-gen AI products at scale.
                  </p>
                  <p className="text-[#4a5568] leading-relaxed mb-8" data-id="element-221">
                    Go beyond single agents. You'll architect a multi-agent AI
                    system—where specialized agents coordinate, reason, and
                    execute complex tasks end-to-end.
                  </p>
                </div>
              </div>

              <div className="px-10 pb-6 mt-auto" data-id="element-222">
                <div className="bg-[#f8fafb] p-6 rounded-xl border-2 border-[#2f5f98] border-opacity-20 min-h-[240px]" data-id="element-223">
                  <p className="text-[#2f5f98] font-bold text-base mb-4" data-id="element-224">
                    What you gain:
                  </p>
                  <ul className="space-y-3" data-id="element-225">
                    <li className="flex items-start" data-id="element-226">
                      <CheckCircle2Icon className="w-5 h-5 text-[#5590bc] mr-3 flex-shrink-0 mt-0.5" data-id="element-227" />
                      <span className="text-[#4a5568] font-medium" data-id="element-228">
                        A portfolio-ready AI PRD that shows strategic depth
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-229">
                      <CheckCircle2Icon className="w-5 h-5 text-[#2f5f98] mr-3 flex-shrink-0 mt-0.5" data-id="element-230" />
                      <span className="text-[#4a5568] font-medium" data-id="element-231">
                        A standout project that signals technical depth +
                        product vision
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-232">
                      <CheckCircle2Icon className="w-5 h-5 text-[#2f5f98] mr-3 flex-shrink-0 mt-0.5" data-id="element-233" />
                      <span className="text-[#4a5568] font-medium" data-id="element-234">
                        The toolkit to lead ambitious AI initiatives
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Duration Footer */}
              <div className="bg-gradient-to-r from-[#2f5f98] to-[#1e4a7a] px-10 py-5 border-t-4 border-[#2f5f98]" data-id="element-235">
                <p className="text-white text-center text-lg" data-id="element-236">
                  <span className="font-bold" data-id="element-237">2–3 weeks</span>
                  <span className="mx-2" data-id="element-238">•</span>
                  <span data-id="element-239">⭐ Members only</span>
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-240">
            <p className="text-[#4a5568] text-lg mb-3" data-id="element-241">
              💡 Not sure which track to choose? Join our Track Selection
              Workshop for guidance!
            </p>
            <p className="text-[#2f5f98] font-bold text-lg" data-id="element-242">
              Want access to all tracks?{' '}
              <a href="https://www.productcompass.pm/" target="_blank" rel="noopener noreferrer" className="text-[#ff8a14] hover:underline" data-id="element-243">
                Join The Product Compass
              </a>
            </p>
          </div>
        </div>
      </div>
      {/* What You Get Section - Full Width */}
      <div className="w-full bg-[#5590bc] text-white py-16 px-4 shadow-md mb-16" data-id="element-244">
        <div className="max-w-6xl mx-auto" data-id="element-245">
          <div className="text-center mb-10" data-id="element-246">
            <h2 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-247">
              What You'll Walk Away With
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12" data-id="element-248">
            {/* Core Experience Column */}
            <div data-id="element-249">
              <h3 className="text-2xl font-bold mb-6 pb-2 border-b border-[#5c8bc0]" data-id="element-250">
                Core Experience
              </h3>
              <div className="space-y-6" data-id="element-251">
                <div className="flex items-start" data-id="element-252">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-253" />
                  <p data-id="element-254">A portfolio-ready AI product prototype</p>
                </div>
                <div className="flex items-start" data-id="element-255">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-256" />
                  <p data-id="element-257">
                    Hands-on experience with in-demand AI tools: n8n, Lovable,
                    and more.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-258">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-259" />
                  <p data-id="element-260">
                    Get a personalized feedback report from senior product
                    leaders on your prototype, strategy, and execution.
                  </p>
                </div>
              </div>
            </div>
            {/* Career Launchpad Column */}
            <div data-id="element-261">
              <h3 className="text-2xl font-bold mb-6 pb-2 border-b border-[#5c8bc0]" data-id="element-262">
                Career Launchpad
              </h3>
              <div className="space-y-6" data-id="element-263">
                <div className="flex items-start" data-id="element-264">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-265" />
                  <p data-id="element-266">
                    Earn{' '}
                    <span className="font-semibold" data-id="element-267">
                      an official certificate from The Product Compass
                    </span>{' '}
                    —recognized by hiring teams in the AI space
                  </p>
                </div>
                <div className="flex items-start" data-id="element-268">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-269" />
                  <p data-id="element-270">
                    Top 5 teams get spotlighted in our AI PM Talent Showcase
                    (seen by recruiters from leading AI teams)
                  </p>
                </div>
                <div className="flex items-start" data-id="element-271">
                  <CheckCircle2Icon className="w-6 h-6 text-[#f7f7f7] mr-3 flex-shrink-0 mt-0.5" data-id="element-272" />
                  <p data-id="element-273">
                    Go from zero visibility to top of the AI PM hotlist—and
                    finally stand out in a crowded job market.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Team Section */}
      <TeamSection data-id="element-274" />
      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 mb-16" data-id="element-275">
        <div className="text-center mb-12" data-id="element-276">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2f5f98] mb-2" data-id="element-277">
            FAQs
          </h2>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-278">
          {faqs.map((faq, index) => <div key={index} data-id="element-279">
              <button onClick={() => toggleFaq(index)} className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" data-id="element-280">
                <h3 className="text-lg md:text-xl font-bold text-[#1a1a1a] pr-4" data-id="element-281">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0" data-id="element-282">
                  {openFaqIndex === index ? <div className="w-8 h-8 flex items-center justify-center text-2xl font-light text-[#2f5f98]" data-id="element-283">
                      −
                    </div> : <div className="w-8 h-8 flex items-center justify-center text-2xl font-light text-[#2f5f98]" data-id="element-284">
                      +
                    </div>}
                </div>
              </button>
              {openFaqIndex === index && <div className="px-8 pb-6 text-[#202929] animate-fadeIn" data-id="element-285">
                  {faq.answer}
                </div>}
              {index < faqs.length - 1 && <div className="mx-8 border-t border-dashed border-gray-300" data-id="element-286"></div>}
            </div>)}
        </div>
        {/* Still have questions */}
        <div className="mt-8 bg-gradient-to-r from-[#5590bc] to-[#2f5f98] p-8 rounded-lg shadow-md text-center" data-id="element-287">
          <h3 className="text-2xl font-bold text-white mb-2" data-id="element-288">
            Still have questions?
          </h3>
          <p className="text-white" data-id="element-289">
            Get clarity at the Track Selection Workshop — register now.
          </p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4" data-id="element-290">
        {/* Final CTA */}
        <div className="text-center" data-id="element-291">
          <h3 className="text-4xl md:text-5xl font-bold text-[#2f5f98] mb-6" data-id="element-292">
            Ready to accelerate your AI career?
          </h3>
          <a href="#register" className="inline-flex items-center bg-gradient-to-r from-[#ff8a14] to-[#e67d12] hover:from-[#e67d12] hover:to-[#d57010] text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-colors" data-id="element-293">
            Join the Buildathon NOW
            <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-294" />
          </a>
          <p className="mt-4 text-[#606060]" data-id="element-295">
            <span className="text-[#00b3b1] font-semibold" data-id="element-296">
              The Future of Product Is AI
            </span>{' '}
            —Secure yours now.
          </p>
        </div>
      </div>
    </div>;
}